import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
export interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  referenceNumber?: string;
  applicationId?: string;
  serviceId?: string;
  mdaId?: string;
  channels: ('email' | 'sms' | 'in_app')[];
}
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  getNotificationsByUserId: (userId: string) => Notification[];
  markAsRead: (notificationId: string) => void;
  markAllAsRead: (userId: string) => void;
  createNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  deleteNotification: (notificationId: string) => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
// Sample notifications
const sampleNotifications: Notification[] = [{
  id: uuidv4(),
  userId: 'user-001',
  type: 'application_submitted',
  title: 'Application Submitted',
  message: 'Your application for Tax Clearance Certificate has been submitted successfully. Reference Number: TAX-2023-1001',
  createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  read: false,
  referenceNumber: 'TAX-2023-1001',
  applicationId: 'app-001',
  serviceId: '101',
  mdaId: 'mda-001',
  channels: ['email', 'in_app']
}, {
  id: uuidv4(),
  userId: 'user-001',
  type: 'payment_completed',
  title: 'Payment Successful',
  message: 'Your payment of ₦2,500 for Tax Clearance Certificate has been received. Your application is now being processed.',
  createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  read: true,
  referenceNumber: 'TAX-2023-1001',
  applicationId: 'app-001',
  serviceId: '101',
  mdaId: 'mda-001',
  channels: ['email', 'sms', 'in_app']
}, {
  id: uuidv4(),
  userId: 'user-001',
  type: 'application_approved',
  title: 'Application Approved',
  message: 'Your application for Tax Clearance Certificate has been approved. You can now download your certificate.',
  createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
  read: false,
  referenceNumber: 'TAX-2023-1001',
  applicationId: 'app-001',
  serviceId: '101',
  mdaId: 'mda-001',
  channels: ['email', 'sms', 'in_app']
}];
export const NotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // Load notifications from localStorage or use sample data
  useEffect(() => {
    const savedNotifications = localStorage.getItem('notifications');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    } else {
      setNotifications(sampleNotifications);
      localStorage.setItem('notifications', JSON.stringify(sampleNotifications));
    }
  }, []);
  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);
  // Get unread count
  const unreadCount = notifications.filter(n => !n.read).length;
  // Get notifications by user ID
  const getNotificationsByUserId = useCallback((userId: string) => {
    return notifications.filter(notification => notification.userId === userId);
  }, [notifications]);
  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => prev.map(notification => notification.id === notificationId ? {
      ...notification,
      read: true
    } : notification));
  }, []);
  // Mark all notifications as read for a user
  const markAllAsRead = useCallback((userId: string) => {
    setNotifications(prev => prev.map(notification => notification.userId === userId ? {
      ...notification,
      read: true
    } : notification));
  }, []);
  // Create a new notification
  const createNotification = useCallback((notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [...prev, newNotification]);
    // In a real app, you would also handle sending via email/SMS here
    console.log(`Notification created: ${newNotification.title}`);
    if (newNotification.channels.includes('email')) {
      console.log(`Email would be sent: ${newNotification.title}`);
    }
    if (newNotification.channels.includes('sms')) {
      console.log(`SMS would be sent: ${newNotification.title}`);
    }
    return newNotification.id;
  }, []);
  // Delete a notification
  const deleteNotification = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
  }, []);
  return <NotificationContext.Provider value={{
    notifications,
    unreadCount,
    getNotificationsByUserId,
    markAsRead,
    markAllAsRead,
    createNotification,
    deleteNotification
  }} data-id="element-818">
      {children}
    </NotificationContext.Provider>;
};
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};